import * as React from 'react';

import { EStatus, TCheckContactsAccessParams, TContext, TEarlyAccessPaywallParams } from '../../types/context';
import { IEarlyAccessPaywall } from '../../types/earlyAccessPaywallModal';
import { TFetchManifestResult } from '../../types/microfrontend';
import { context as Context } from '../../utils/context';

import type { ICianMFUIElement } from '@cian/mf-registry/browser';
import type { IMicrofrontendManifest } from '@cian/mf-registry/shared';

interface IProviderProps {
  fetchManifest: () => Promise<TFetchManifestResult>;
  params: TEarlyAccessPaywallParams;
}

export enum EAccessCheckTrackingStatus {
  Granted = 'granted',
  Unauthorized = 'unauthorized',
  Unsubscribed = 'unsubscribed',
  NotSearcher = 'notsearcher',
  PaymentPending = 'paymentpending',
  Error = 'error',
}

export type TAccessCheckResult = {
  isConfirmed: boolean;
  trackingStatus: EAccessCheckTrackingStatus | null;
};

export const CLOSE_MODAL = '/v1/close-modal';
export const CHECK_CONTACTS_ACCESS = '/v1/check-contacts-access';

export const Provider: React.FC<React.PropsWithChildren<IProviderProps>> = ({ children, fetchManifest, params }) => {
  const microfrontendRef = React.useRef<ICianMFUIElement | null>(null);
  const [manifest, setManifest] = React.useState<IMicrofrontendManifest | null>(null);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [lastUsedOfferId, setLastUsedOfferId] = React.useState<number | null>(null);

  const getLocalApi = React.useCallback(() => {
    const api = (microfrontendRef.current?.instance?.customElement as IEarlyAccessPaywall)?.api;
    if (!api) {
      throw new Error(
        'Не удалось получить доступ к локальному API микрофронтенда "early-access-paywall-microfrontend"',
      );
    }

    return api;
  }, []);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const context = React.useMemo<TContext>(() => {
    const commonParameters = {
      microfrontendRef,
      params: {
        ...params,
      },
      open,
      onClose: handleClose,
      lastUsedOfferId,
      checkContactsAccess: async ({ offerId, limitType }: TCheckContactsAccessParams): Promise<TAccessCheckResult> => {
        // Сохраняем offerId для использования в FallbackModalContainer
        setLastUsedOfferId(offerId);

        if (!manifest) {
          setLoading(true);
          const result = await fetchManifest();
          setLoading(false);

          if (result) {
            setManifest(result.manifest);
          } else {
            setOpen(true);
            throw new Error('Не удалось загрузить манифест микрофронтенда "early-access-paywall-microfrontend"');
          }

          // Ждём пока микрофронт замаунтится
          await new Promise(resolve => setTimeout(resolve, 0));
        }

        const { isConfirmed, trackingStatus } = await getLocalApi()[CHECK_CONTACTS_ACCESS]({ offerId, limitType });
        setLoading(false);

        return { isConfirmed, trackingStatus };
      },
    };

    if (loading) {
      return {
        ...commonParameters,
        status: EStatus.Loading,
      };
    }

    if (!manifest) {
      return {
        ...commonParameters,
        status: EStatus.Failed,
      };
    }

    return {
      ...commonParameters,
      manifest,
      status: EStatus.Succeed,
    };
  }, [params, open, handleClose, loading, manifest, getLocalApi, fetchManifest, lastUsedOfferId]);

  return <Context.Provider value={context}>{children}</Context.Provider>;
};
